import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

const steps = [
  {
    n: "1",
    title: "Set up with a trusted mechanic",
    desc: "Get your bike set up initially by a pre-verified mechanic near you.",
  },
  {
    n: "2",
    title: "Get replacement-window updates",
    desc: "Receive timely updates about upcoming replacement windows, right here in the Raahi app.",
  },
  {
    n: "3",
    title: "Save through Parts E-Dukaan",
    desc: "Save on OE-branded parts via our Parts E-Dukaan, and order them directly to your mechanic.",
  },
];

export default function Journey() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.8", "end 0.6"],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      data-testid="journey-section"
      className="relative py-24 md:py-32 px-6 md:px-12 bg-[#16191F]"
    >
      <div className="max-w-2xl mx-auto">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mb-16"
        >
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#E7B459] mb-6">
            03 — The journey
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            From uncertainty to clarity.
          </h2>
        </motion.div>

        <div ref={ref} className="relative pl-14 md:pl-16">
          {/* track */}
          <div className="absolute left-[19px] md:left-[23px] top-2 bottom-2 w-[2px] bg-white/10" />
          <motion.div
            style={{ height: lineHeight }}
            className="absolute left-[19px] md:left-[23px] top-2 w-[2px] bg-gradient-to-b from-[#E7B459] to-[#D9544E]"
          />

          <motion.ol
            variants={stagger(0.18)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="space-y-14"
          >
            {steps.map((s) => (
              <motion.li
                key={s.n}
                variants={fadeUp}
                data-testid={`journey-step-${s.n}`}
                className="relative"
              >
                <span className="absolute -left-14 md:-left-16 top-0 flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full bg-[#2D323D] border border-[#E7B459]/50 font-display font-bold text-[#E7B459] shadow-[0_0_20px_rgba(231,180,89,0.25)]">
                  {s.n}
                </span>
                <h3 className="font-display text-xl sm:text-2xl font-medium tracking-tight mb-2">
                  {s.title}
                </h3>
                <p className="text-base leading-relaxed text-[#F4F1ED]/65">
                  {s.desc}
                </p>
              </motion.li>
            ))}
          </motion.ol>
        </div>
      </div>
    </section>
  );
}
